import { useNavigation } from "@react-navigation/native";
import type { NativeStackScreenProps } from "@react-navigation/native-stack";
import { observer } from "mobx-react-lite";
import React from "react";
import { useApi } from "../../../core/apis";
import { useStores } from "../../../core/stores";
import { Box } from "../../../ui/Box";
import { FullScreenLoading } from "../../../ui/Loading";
import { BusinessModuleType } from "../../modules/types";
import { BusinessAdminTitleAndEmojiEdit } from "../../modules/ui/BusinessAdminEmojiPicker";
import { BusinessAdminView } from "../BusinessAdminView";
import { BusinessAdminTemplateTasks } from "./BusinessAdminTemplateTasks";

type Props = NativeStackScreenProps<
  ReactNavigation.RootParamList,
  "CustomerAdminTemplateSectionEdit"
>;

export const BusinessAdminTemplateSectionEditScreen = observer(
  ({ route }: Props) => {
    const customerUid = route.params.customerUid;
    const templateUid = route.params.templateUid;
    const sectionUid = route.params.sectionUid;
    const navigation = useNavigation();
    const { businessTemplatesApi } = useApi();
    const { businessTemplatesStore } = useStores();

    const template = businessTemplatesStore.byId.get(templateUid);
    const sectionData = template?.data.sections.find(
      (s) => s.uid === sectionUid,
    );

    React.useEffect(() => {
      return businessTemplatesApi.watchItem(templateUid);
    }, [templateUid, businessTemplatesApi]);

    if (!template || !sectionData) {
      return <FullScreenLoading />;
    }

    return (
      <BusinessAdminView customerUid={customerUid}>
        <Box>
          <BusinessAdminTitleAndEmojiEdit
            formValues={sectionData}
            onChange={(f) =>
              template.updateBusinessTemplateSection({
                ...sectionData,
                title: f.title ?? "",
                emoji: f.emoji ?? null,
              })
            }
          />
        </Box>

        <BusinessAdminTemplateTasks
          title={"Items"}
          overrideTypes={[BusinessModuleType.Info]}
          businessModules={sectionData.businessModules}
          onSave={(moduleData) => {
            return template.addBusinessTemplateModuleToSection(
              sectionData,
              moduleData,
            );
          }}
          onEdit={(t) => {
            navigation.navigate("CustomerAdminTemplateSectionModuleEdit", {
              customerUid,
              templateUid,
              sectionUid: sectionData.uid,
              moduleUid: t.uid,
            });
          }}
          onOrderChange={(items) => {
            return template.updateBusinessTemplateSectionModuleOrder(
              sectionData,
              items,
            );
          }}
          onDelete={(t) => {
            return template.deleteBusinessTemplateSectionModule(sectionData, t);
          }}
        />
      </BusinessAdminView>
    );
  },
);
